import dayjs from "dayjs";
import enLocale from "dayjs/locale/en";
import svLocale from "dayjs/locale/sv";
import moment from "moment";
import "moment/locale/sv";
import { DATE_TIME_FORMAT_TYPES } from "../configs/constants/DateTimeFormatTypes";
import { DateOrString } from "../models/Job";
import { convertToSentenceCase } from "./string.utils";

export const formatToWeekDay = (date: string | Date) => {
	setLocale();
	return moment(date).format("ddd");
};

export const getWeekNo = (date: string | Date) => {
	return moment(date).week();
};

export const getTime = (date: string | Date) => {
	return `${moment(date).hour()}:${moment(date).minute()}`;
};

export const isFuture = (date: string | Date) => {
	return moment(date).isAfter(new Date());
};

export const contactDateAndTime = (date: string | Date, time: string) => {
	const [hours, minutes] = time.split(":").map(Number);
	const localDateTime = moment(date).startOf("day").hour(hours).minute(minutes);

	return localDateTime.toISOString();
};

/**
 * Builds a start/end pair from two "HH:mm" times, both anchored on the START
 * date. An end time earlier than the start time wraps past midnight to the
 * following day (e.g. 20:00 - 00:00).
 *
 * Never anchor the end on the previous end date: a report that ended at/after
 * midnight has its end on the day after the start, and re-anchoring a
 * corrected same-day evening time on that date silently adds 24 hours to the
 * reported window.
 */
export const contactTimeRange = (startDate: string | Date, startTime: string, endTime: string) => {
	const start = moment(contactDateAndTime(startDate, startTime));
	let end = moment(contactDateAndTime(startDate, endTime));

	if (end.isBefore(start)) end = end.add(1, "day");

	return { start: start.toISOString(), end: end.toISOString() };
};

export const setLocale = (theMoment = moment) => {
	const lang = window.navigator.language || "en";
	theMoment.locale(lang.substr(0, 2));
};

export const getBrowserLocale = () => {
	if (window.navigator.language.includes("sv")) {
		return svLocale;
	}
	return enLocale;
};

export const getAmPm = (time24: string) => {
	return moment(time24, "HH:mm").format("hh:mm A");
};

export const getAgeBySNN = (snn: string) => {
	return moment().diff(moment(snn.substring(0, 8), "YYYYMMDD"), "years");
};

export const getFromToWeekdayDate = (from: Date | string, to: Date | string) => {
	const fromStr = moment(from).format(DATE_TIME_FORMAT_TYPES.DATE_WEEKDAY_MONTH);
	const toStr = moment(to).format(DATE_TIME_FORMAT_TYPES.DATE_WEEKDAY_MONTH);

	if (fromStr === toStr) {
		return convertToSentenceCase(fromStr);
	} else {
		return `${convertToSentenceCase(fromStr)} - ${toStr}`;
	}
};

export const formatTimestamp = (date: string | Date) => {
	return moment(date).format(DATE_TIME_FORMAT_TYPES.DATE_TIME);
};

export const formatDate = (date: string | Date) => {
	return convertToSentenceCase(moment(date).format(DATE_TIME_FORMAT_TYPES.DATE_WEEKDAY_MONTH));
};

export const shiftTimeFromTo = (shift?: { start: DateOrString; end: DateOrString }) => {
	if (!shift) return "";
	return moment(shift.start).format("HH:mm") + " - " + moment(shift.end).format("HH:mm");
};

export const jobFromTo = (startDate: string | Date, endDate: string | Date) => {
	setLocale();
	const mStart = moment(startDate);
	const mEnd = moment(endDate);

	if (mStart.isSame(mEnd, "day")) {
		return convertToSentenceCase(
			moment(startDate).format("ddd DD MMM, HH:mm") + " - " + moment(endDate).format("HH:mm")
		);
	} else {
		return convertToSentenceCase(
			moment(startDate).format("ddd DD MMM, HH:mm") +
				" - " +
				moment(endDate).format("ddd DD MMM, HH:mm")
		);
	}
};

export const shiftsHours = (
	shifts: { start: string | Date; end: string | Date; break?: number }[]
) => {
	const { totHours, totBreakHours } = shifts.reduce(
		(tot, curr) => {
			tot.totHours += shiftHours(curr.start, curr.end, curr.break);
			tot.totBreakHours += curr.break || 0;
			return tot;
		},
		{ totHours: 0, totBreakHours: 0 }
	);

	return {
		totHours,
		totBreakHours,
	};
};

export const shiftHours = (start: string | Date, end: string | Date, breakHours?: number) => {
	const hours = moment(end).diff(start, "minutes") / 60;
	return hours - (breakHours || 0);
};

export function dateRangeToDays(start: string | Date, end: string | Date) {
	let pointer = dayjs(start);
	let out = [];

	if (dayjs(start).isSame(end, "day")) {
		return [
			{
				start: dayjs(start).toDate(),
				end: dayjs(end).toDate(),
			},
		];
	}

	const endDate = dayjs(end).endOf("day");

	while (pointer.isBefore(endDate)) {
		out.push({
			start: pointer.toDate(),
			end: pointer.endOf("day").toDate(),
		});

		pointer = pointer.add(1, "day").startOf("day");
	}

	return out;
}

export function isSameTime(time1: string | Date, time2: string | Date) {
	return moment(time1).format("HHmm") === moment(time2).format("HHmm");
}

export function getYearList(
	prevYearCount: number,
	nextYearCount: number
): { name: string; key: string }[] {
	const currentYear = new Date().getFullYear();
	const startYear = currentYear - prevYearCount;
	const endYear = currentYear + nextYearCount;

	const yearList: { name: string; key: string }[] = [];
	for (let year = startYear; year <= endYear; year++) {
		yearList.push({ name: year.toString(), key: year.toString() });
	}

	return yearList;
}

export function getWeekNumberList(year: number): { name: string; key: string }[] {
	const weekNumberList: number[] = [];
	const date = new Date(year, 0, 1);
	const endOfYear = new Date(year, 11, 31);

	// Loop through each week of the year
	while (date <= endOfYear) {
		const weekNumber = getWeekNumber(date);
		if (!weekNumberList.includes(weekNumber)) {
			weekNumberList.push(weekNumber);
		}
		date.setDate(date.getDate() + 7); // Move to the next week
	}

	return weekNumberList
		.map((w) => ({ key: w.toString(), name: w.toString() }))
		.filter((week) => week.key !== "0");
}

// Helper function to get the week number of a given date
export function getWeekNumber(date: Date): number {
	const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
	const millisecondsInWeek = 604800000; // 7 * 24 * 60 * 60 * 1000
	return Math.ceil((date.getTime() - firstDayOfYear.getTime()) / millisecondsInWeek);
}

export function formatCurrencySEK(number: number): string {
	return new Intl.NumberFormat("sv-SE", {
		style: "currency",
		currency: "SEK",
		currencyDisplay: "code",
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(number);
}

export function formatNumber(number: number, minimumFractionDigits = 2): string {
	if (isNaN(number)) return "";
	return new Intl.NumberFormat("sv-SE", {
		minimumFractionDigits,
		maximumFractionDigits: 2,
	}).format(number);
}

export function setStartOfWorkday(date: Date) {
	return dayjs(date).startOf("day").add(5, "hours").toDate();
}

/**
 * Translates English weekday names to Swedish
 * @param weekday - English weekday name (e.g., "monday", "Wednesday")
 * @returns Swedish weekday name (e.g., "måndag", "onsdag")
 */
export function translateWeekday(weekday: string): string {
	const weekdayMap: Record<string, string> = {
		monday: "måndag",
		tuesday: "tisdag",
		wednesday: "onsdag",
		thursday: "torsdag",
		friday: "fredag",
		saturday: "lördag",
		sunday: "söndag",
	};

	const normalizedWeekday = weekday.toLowerCase();
	return weekdayMap[normalizedWeekday] || weekday;
}
